import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Tag, Star, ShoppingCart, 
  Truck, Shield, CheckCircle, ChevronRight,
  Heart, Share2, Eye, Edit2, Trash2,
  Loader2, AlertCircle, XCircle, CheckCircle2,
  Image as ImageIcon, Layers, Palette, Ruler, Package2,
  Calendar, RefreshCw, DollarSign, BarChart3,
  Grid3x3, ShoppingBag, TrendingUp
} from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  const API_BASE = 'https://api.maechi.in';

  // Fetch product details
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await fetch(`${API_BASE}/products?id=${id}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setProduct(data.data);
          if (data.data.variants && data.data.variants.length > 0) {
            setSelectedVariant(data.data.variants[0]);
          }
        } else {
          setError(data.message || 'Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Handle delete product
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/products?id=${id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Product deleted successfully');
        setTimeout(() => {
          navigate('/products');
        }, 1500);
      } else {
        setError(data.message || 'Failed to delete product');
      }
    } catch (err) {
      console.error('Delete Error:', err);
      setError('Failed to delete product');
    }
  };

  // Handle quantity change
  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= (selectedVariant?.stock_quantity || product?.stock_quantity || 100)) {
      setQuantity(newQuantity);
    }
  };

  // Handle add to cart
  const handleAddToCart = () => {
    const item = {
      id: selectedVariant?.id || product?.id,
      productId: product?.id,
      name: product?.name,
      variant: selectedVariant,
      price: selectedVariant?.price || product?.price,
      quantity: quantity,
      image: product?.images?.[0]?.url || product?.image
    };
    
    // Add to cart logic here (localStorage or API call)
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItemIndex = cart.findIndex(item => item.id === (selectedVariant?.id || product?.id));
    
    if (existingItemIndex > -1) {
      cart[existingItemIndex].quantity += quantity;
    } else {
      cart.push(item);
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    setSuccessMessage('Product added to cart');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!product) return 0;
    if (product.compare_price > product.price) {
      return Math.round(((product.compare_price - product.price) / product.compare_price) * 100);
    }
    return 0;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </button>
        
        <div className="text-center py-16">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The product you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  const discount = calculateDiscount();
  const currentPrice = selectedVariant?.price || product.price;
  const currentStock = selectedVariant?.stock_quantity || product.stock_quantity;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center shadow-lg max-w-md">
          <CheckCircle2 className="w-5 h-5 text-green-600 mr-3" />
          <span className="text-green-800">{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="ml-auto">
            <XCircle className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}
      
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center shadow-lg max-w-md">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <XCircle className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/products')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(`/products/edit/${id}`)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              >
                <Edit2 size={16} className="mr-2" />
                Edit Product
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </button>
            </div>
          </div>
          
          {/* Breadcrumb */}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <span>Products</span>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span>{product.category_name || 'Uncategorized'}</span>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Images */}
          <div>
            {/* Main Image */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
              <div className="aspect-square rounded-lg bg-gray-100 overflow-hidden">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[selectedImage]?.url || product.image}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-gray-300" />
                  </div>
                )}
              </div>
              
              {/* Image Gallery */}
              {product.images && product.images.length > 1 && (
                <div className="mt-4 flex space-x-2 overflow-x-auto pb-2">
                  {product.images.map((img, index) => (
                    <button
                      key={img.id}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden ${
                        selectedImage === index 
                          ? 'border-blue-500' 
                          : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={`${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Product Statistics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{product.stock_quantity}</div>
                  <div className="text-sm text-gray-600">Current Stock</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {product.low_stock_threshold || 5}
                  </div>
                  <div className="text-sm text-gray-600">Low Stock Alert</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {product.cost ? formatPrice(product.cost) : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Cost Price</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {discount}%
                  </div>
                  <div className="text-sm text-gray-600">Discount</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div>
            {/* Product Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center mb-2">
                    {product.featured && (
                      <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded mr-2">
                        Featured
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      product.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : product.status === 'out_of_stock'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.status === 'out_of_stock' ? 'Out of Stock' : 
                       product.status === 'draft' ? 'Draft' : 'In Stock'}
                    </span>
                  </div>
                  
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
                  <p className="text-gray-500">SKU: {product.sku}</p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-red-500">
                    <Heart className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-blue-500">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Price Section */}
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatPrice(currentPrice)}
                  </span>
                  {product.compare_price > product.price && (
                    <>
                      <span className="ml-3 text-xl text-gray-500 line-through">
                        {formatPrice(product.compare_price)}
                      </span>
                      <span className="ml-3 px-2 py-1 bg-red-100 text-red-800 text-sm font-bold rounded">
                        Save {discount}%
                      </span>
                    </>
                  )}
                </div>
                
                {/* Stock Status */}
                <div className="flex items-center mb-4">
                  {currentStock > 0 ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span>In Stock ({currentStock} available)</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600">
                      <AlertCircle className="w-5 h-5 mr-2" />
                      <span>Out of Stock</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Variants */}
              {product.variants && product.variants.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Available Variants</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map(variant => (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        className={`px-4 py-2 border rounded-lg flex items-center ${
                          selectedVariant?.id === variant.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {variant.attributes?.color?.hex_code && (
                          <div 
                            className="w-4 h-4 rounded-full mr-2 border"
                            style={{ backgroundColor: variant.attributes.color.hex_code }}
                          />
                        )}
                        <span>{variant.sku}</span>
                        <span className="ml-2 font-medium">
                          {formatPrice(variant.price)}
                        </span>
                        {variant.stock_quantity <= 0 && (
                          <span className="ml-2 text-xs text-red-600">(Out of Stock)</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Variant Details */}
              {selectedVariant && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Selected Variant</h4>
                      <p className="text-sm text-gray-600">SKU: {selectedVariant.sku}</p>
                      {selectedVariant.attributes && Object.keys(selectedVariant.attributes).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {Object.entries(selectedVariant.attributes).map(([key, attr]) => (
                            <div key={key} className="text-sm">
                              <span className="text-gray-500">{attr.name}: </span>
                              {attr.hex_code ? (
                                <div className="inline-flex items-center">
                                  <div 
                                    className="w-3 h-3 rounded-full mr-1 border"
                                    style={{ backgroundColor: attr.hex_code }}
                                  />
                                  <span>{attr.value}</span>
                                </div>
                              ) : (
                                <span className="font-medium">{attr.value}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatPrice(selectedVariant.price)}
                      </div>
                      <div className="text-sm text-gray-600">
                        Stock: {selectedVariant.stock_quantity}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Add to Cart Section */}
              <div className="mb-6">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      className="px-4 py-3 text-gray-600 hover:text-gray-900"
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      min="1"
                      max={currentStock}
                      className="w-16 text-center border-0 focus:ring-0"
                    />
                    <button
                      onClick={() => handleQuantityChange(1)}
                      className="px-4 py-3 text-gray-600 hover:text-gray-900"
                      disabled={quantity >= currentStock}
                    >
                      +
                    </button>
                  </div>
                  
                  <button
                    onClick={handleAddToCart}
                    disabled={currentStock <= 0}
                    className={`flex-1 py-3 rounded-lg flex items-center justify-center ${
                      currentStock > 0
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {currentStock > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Truck className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <p className="font-medium">Free Shipping</p>
                    <p className="text-sm text-gray-600">On orders over ₹2999</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="font-medium">2 Year Warranty</p>
                    <p className="text-sm text-gray-600">Quality guarantee</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Details Tabs */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex">
                  <button className="px-6 py-3 border-b-2 border-blue-500 text-blue-600 font-medium">
                    Description
                  </button>
                  <button className="px-6 py-3 text-gray-600 hover:text-gray-900">
                    Specifications
                  </button>
                  <button className="px-6 py-3 text-gray-600 hover:text-gray-900">
                    Reviews (0)
                  </button>
                </nav>
              </div>
              
              <div className="p-6">
                <div className="prose max-w-none">
                  {product.description ? (
                    <div dangerouslySetInnerHTML={{ __html: product.description }} />
                  ) : (
                    <p className="text-gray-500">No description provided.</p>
                  )}
                  
                  {/* Product Specifications */}
                  <div className="mt-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Specifications</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Category</p>
                        <p className="font-medium">{product.category_name || 'Uncategorized'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Brand</p>
                        <p className="font-medium">{product.brand || 'Not specified'}</p>
                      </div>
                      {product.weight > 0 && (
                        <div>
                          <p className="text-sm text-gray-500">Weight</p>
                          <p className="font-medium">{product.weight} kg</p>
                        </div>
                      )}
                      {product.dimensions && (
                        <div>
                          <p className="text-sm text-gray-500">Dimensions</p>
                          <p className="font-medium">{product.dimensions}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Package className="w-6 h-6 text-blue-600 mr-3" />
              <h3 className="font-medium text-gray-900">Product Information</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="font-medium">
                  {new Date(product.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last Updated</span>
                <span className="font-medium">
                  {new Date(product.updated_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span className={`font-medium ${
                  product.status === 'active' ? 'text-green-600' : 
                  product.status === 'out_of_stock' ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <DollarSign className="w-6 h-6 text-green-600 mr-3" />
              <h3 className="font-medium text-gray-900">Pricing Details</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Selling Price</span>
                <span className="font-medium">{formatPrice(product.price)}</span>
              </div>
              {product.compare_price > product.price && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Compare Price</span>
                  <span className="font-medium text-gray-500 line-through">
                    {formatPrice(product.compare_price)}
                  </span>
                </div>
              )}
              {product.cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost Price</span>
                  <span className="font-medium">{formatPrice(product.cost)}</span>
                </div>
              )}
              {product.cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Profit Margin</span>
                  <span className="font-medium text-green-600">
                    {Math.round(((product.price - product.cost) / product.cost) * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <TrendingUp className="w-6 h-6 text-purple-600 mr-3" />
              <h3 className="font-medium text-gray-900">Inventory</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Stock</span>
                <span className={`font-medium ${
                  product.stock_quantity > 10 ? 'text-green-600' : 
                  product.stock_quantity > 0 ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {product.stock_quantity} units
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Low Stock Alert</span>
                <span className="font-medium">
                  {product.low_stock_threshold || 5} units
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Variants</span>
                <span className="font-medium">
                  {product.variants?.length || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={() => navigate(`/products/edit/${id}`)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Edit2 className="w-5 h-5 mr-2" />
            Edit Product
          </button>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back to Products
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;